import React from "react";
import { 
  CheckCircle, 
  AlertCircle, 
  XCircle, 
  HelpCircle, 
  Info
} from "lucide-react";
import { 
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";

export interface MetricRatingThresholds {
  green: number | [number, number] | string;
  yellow: [number, number] | string;
  red: number | [number, number] | string;
  unit?: string;
  higherIsBetter?: boolean;
  formatValue?: (value: number) => string;
}

export interface MetricDefinition {
  id: string;
  name: string;
  description: string;
  thresholds: MetricRatingThresholds;
  improveText: string;
}

export const METRICS_DEFINITIONS: MetricDefinition[] = [
  {
    id: "cpm",
    name: "CPM (Kosten pro 1.000 Impr.)",
    description: "Durchschnittliche Kosten für 1.000 Impressionen der Anzeige",
    thresholds: {
      green: 25,
      yellow: [25, 45],
      red: 45,
      unit: "€",
      higherIsBetter: false,
    },
    improveText: "Zielgruppe einschränken, relevantere Anzeigen erstellen oder Anzeigenqualität verbessern."
  },
  {
    id: "cpc",
    name: "CPC (Kosten pro Klick)",
    description: "Durchschnittliche Kosten für einen Klick auf die Anzeige",
    thresholds: {
      green: 1.5,
      yellow: [1.5, 3],
      red: 3,
      unit: "€",
      higherIsBetter: false,
    },
    improveText: "Anzeigentext und Bild optimieren, um Relevanz zu erhöhen. Landingpage-Erlebnis verbessern."
  },
  {
    id: "ctr",
    name: "CTR (Click-Through-Rate)",
    description: "Prozentsatz der Nutzer, die nach dem Sehen der Anzeige darauf klicken",
    thresholds: {
      green: 1.5,
      yellow: [0.8, 1.5],
      red: 0.8,
      unit: "%",
      higherIsBetter: true,
    },
    improveText: "Anzeigentexte überarbeiten, aussagekräftigere Bilder verwenden und Call-to-Action verbessern."
  },
  {
    id: "conversionRate",
    name: "Conversion Rate (Landingpage)",
    description: "Prozentsatz der Nutzer, die nach dem Klick auf die Anzeige eine gewünschte Aktion ausführen",
    thresholds: {
      green: 10,
      yellow: [5, 10],
      red: 5,
      unit: "%",
      higherIsBetter: true,
    },
    improveText: "Landingpage optimieren, Formular vereinfachen, Ladezeit verbessern und Value Proposition klarer kommunizieren."
  },
  {
    id: "cpl",
    name: "Cost per Lead (CPL)",
    description: "Durchschnittliche Kosten für die Generierung eines Leads",
    thresholds: {
      green: 60,
      yellow: [60, 100],
      red: 100,
      unit: "€",
      higherIsBetter: false,
    },
    improveText: "Anzeigen und Landingpage-Qualität verbessern. Zielgruppensegmentierung verfeinern oder Angebots-Conversion optimieren."
  },
  {
    id: "roas",
    name: "ROAS / Leadwert-Schätzung",
    description: "Return on Ad Spend - Verhältnis zwischen dem geschätzten Leadwert und den Werbekosten",
    thresholds: {
      green: 4,
      yellow: [2, 4],
      red: 2,
      unit: ":1",
      higherIsBetter: true,
    },
    improveText: "Lead-Qualifizierungsprozess verbessern. Targeting auf Zielgruppen mit höherem Beratungspotenzial ausrichten."
  },
  {
    id: "frequency",
    name: "Frequency (Anzeigenfrequenz)",
    description: "Durchschnittliche Anzahl, wie oft ein Nutzer die Anzeige sieht",
    thresholds: {
      green: [2, 4],
      yellow: [4, 6],
      red: 6,
      unit: "",
      higherIsBetter: false,
    },
    improveText: "Reichweite erhöhen oder Frequenzgrenze setzen. Bei zu hoher Frequenz besteht die Gefahr von Anzeigenmüdigkeit."
  },
  {
    id: "adRelevance",
    name: "Ad Relevance / Feedback Score",
    description: "Bewertung der Anzeigenrelevanz und -qualität durch Meta",
    thresholds: {
      green: "Hoch / Positiv",
      yellow: "Neutral / Gemischt",
      red: "Niedrig / Negativ",
      higherIsBetter: true,
    },
    improveText: "Anzeigeninhalt verbessern, Relevanz zu Zielgruppe erhöhen und nutzerfreundlichere Anzeigenerlebnisse schaffen."
  }
];

// Rating colors
const RATING_COLORS = {
  green: "text-green-500",
  yellow: "text-amber-500",
  red: "text-red-500",
  unknown: "text-gray-400"
};

interface MetricRatingProps {
  metricId: string;
  value: number | string;
  size?: "sm" | "md" | "lg";
  showValue?: boolean;
  showLabel?: boolean;
}

export const MetricRating: React.FC<MetricRatingProps> = ({ 
  metricId, 
  value, 
  size = "md",
  showValue = true,
  showLabel = false
}) => {
  const metric = METRICS_DEFINITIONS.find(m => m.id === metricId);
  
  if (!metric) {
    console.log(`MetricRating: Metric with id ${metricId} not found`);
    return (
      <div className="flex items-center">
        <HelpCircle className="text-gray-400" size={size === "sm" ? 16 : size === "lg" ? 24 : 20} />
        <span className="ml-1 text-gray-400 text-sm">Unbekannte Metrik</span>
      </div>
    );
  }
  
  // Determine rating based on thresholds
  const getRating = (value: number | string, thresholds: MetricRatingThresholds): 'green' | 'yellow' | 'red' | 'unknown' => {
    if (typeof value === 'string' || typeof thresholds.green === 'string') {
      // Handle string-based ratings (like "High", "Medium", "Low")
      if (value === thresholds.green) return 'green';
      if (value === thresholds.yellow) return 'yellow';
      if (value === thresholds.red) return 'red';
      return 'unknown';
    }
    
    const numValue = typeof value === 'number' ? value : parseFloat(value);
    
    if (isNaN(numValue)) return 'unknown';
    
    const { higherIsBetter = true } = thresholds;
    
    // Check if green threshold is a range or single value
    if (Array.isArray(thresholds.green)) {
      const [min, max] = thresholds.green;
      if (numValue >= min && numValue <= max) return 'green';
    } else if (typeof thresholds.green === 'number') {
      if (higherIsBetter && numValue >= thresholds.green) return 'green';
      if (!higherIsBetter && numValue <= thresholds.green) return 'green';
    }
    
    // Check yellow range
    if (Array.isArray(thresholds.yellow)) {
      const [min, max] = thresholds.yellow;
      if ((higherIsBetter && numValue >= min && numValue < max) ||
          (!higherIsBetter && numValue > min && numValue <= max)) {
        return 'yellow';
      }
    }
    
    // Check if red threshold is a range or single value
    if (Array.isArray(thresholds.red)) {
      const [min, max] = thresholds.red;
      if (numValue >= min && numValue <= max) return 'red';
    } else if (typeof thresholds.red === 'number') {
      if (higherIsBetter && numValue <= thresholds.red) return 'red';
      if (!higherIsBetter && numValue >= thresholds.red) return 'red';
    }
    
    return 'unknown';
  };
  
  const formatMetricValue = (value: number | string): string => {
    if (typeof value === 'string') return value;
    
    const { unit = '' } = metric.thresholds;
    
    if (metric.thresholds.formatValue) {
      return metric.thresholds.formatValue(value);
    }
    
    // Format based on unit
    if (unit === '%') {
      return `${value.toFixed(1)}${unit}`;
    } else if (unit === '€') {
      return `${unit}${value.toFixed(2)}`;
    } else if (unit === ':1') {
      return `${value.toFixed(1)}${unit}`;
    }
    
    return `${value}${unit}`;
  };
  
  const rating = getRating(value, metric.thresholds);
  const formattedValue = typeof value === 'number' || typeof value === 'string' ? formatMetricValue(value) : 'N/A';
  
  // Icon based on rating
  const RatingIcon = rating === 'green' 
    ? CheckCircle 
    : rating === 'yellow' 
      ? AlertCircle 
      : rating === 'red' 
        ? XCircle 
        : HelpCircle;
  
  const iconSize = size === "sm" ? 16 : size === "lg" ? 24 : 20;
  const labelClass = size === "sm" ? "text-xs" : size === "lg" ? "text-base" : "text-sm";
  
  return (
    <Popover>
      <PopoverTrigger asChild>
        <div className="flex items-center cursor-help">
          <RatingIcon 
            className={RATING_COLORS[rating]} 
            size={iconSize} 
          />
          
          {showLabel && (
            <span className={`ml-1.5 ${labelClass} font-medium`}>
              {metric.name}:
            </span>
          )}
          
          {showValue && (
            <span className={`ml-1.5 ${labelClass} font-medium ${RATING_COLORS[rating]}`}>
              {formattedValue}
            </span>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0">
        <div className="p-4">
          <div className="flex items-center mb-2">
            <RatingIcon className={RATING_COLORS[rating]} size={16} />
            <h3 className="text-sm font-medium ml-2">{metric.name}</h3>
          </div>
          
          <p className="text-xs text-gray-500 mb-2">{metric.description}</p>
          
          <div className={`p-2 rounded-md text-xs ${
            rating === 'green' 
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' 
              : rating === 'yellow' 
                ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' 
                : rating === 'red' 
                  ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400'
          }`}>
            <div className="font-medium mb-1">
              Aktueller Wert: {formattedValue}
            </div>
            {rating !== 'green' && rating !== 'unknown' && (
              <div>
                <strong>Verbesserungsmöglichkeiten:</strong> {metric.improveText}
              </div>
            )}
            {rating === 'green' && (
              <div>
                <strong>Ausgezeichnet!</strong> Dieser Wert ist optimal für Immobilien-Kampagnen.
              </div>
            )}
          </div>
        </div>
        
        <Separator />
        
        <div className="p-4">
          <h4 className="text-xs font-medium mb-2">Bewertungsskala:</h4>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="flex items-center">
              <CheckCircle className="text-green-500 mr-1" size={14} />
              <span>
                {typeof metric.thresholds.green === 'string' 
                  ? metric.thresholds.green 
                  : Array.isArray(metric.thresholds.green)
                    ? `${metric.thresholds.green[0]} - ${metric.thresholds.green[1]}${metric.thresholds.unit}`
                    : metric.thresholds.higherIsBetter 
                      ? `> ${metric.thresholds.green}${metric.thresholds.unit}`
                      : `< ${metric.thresholds.green}${metric.thresholds.unit}`}
              </span>
            </div>
            <div className="flex items-center">
              <AlertCircle className="text-amber-500 mr-1" size={14} />
              <span>
                {typeof metric.thresholds.yellow === 'string' 
                  ? metric.thresholds.yellow
                  : `${metric.thresholds.yellow[0]} - ${metric.thresholds.yellow[1]}${metric.thresholds.unit}`}
              </span>
            </div>
            <div className="flex items-center">
              <XCircle className="text-red-500 mr-1" size={14} />
              <span>
                {typeof metric.thresholds.red === 'string' 
                  ? metric.thresholds.red
                  : Array.isArray(metric.thresholds.red)
                    ? `${metric.thresholds.red[0]} - ${metric.thresholds.red[1]}${metric.thresholds.unit}`
                    : metric.thresholds.higherIsBetter 
                      ? `< ${metric.thresholds.red}${metric.thresholds.unit}`
                      : `> ${metric.thresholds.red}${metric.thresholds.unit}`}
              </span>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export interface CampaignHealthScoreProps {
  metrics: {
    cpm?: number;
    cpc?: number;
    ctr?: number;
    conversionRate?: number;
    cpl?: number;
    roas?: number;
    frequency?: number;
    adRelevance?: string;
  };
  showLabels?: boolean;
  compact?: boolean;
}

export const CampaignHealthScore: React.FC<CampaignHealthScoreProps> = ({ 
  metrics,
  showLabels = false,
  compact = false
}) => {
  console.log("CampaignHealthScore rendering with metrics:", metrics);
  
  // Calculate overall health score (0-100%)
  const calculateHealthScore = () => {
    const scores: Record<string, number> = {
      green: 100,
      yellow: 50,
      red: 0,
      unknown: 0
    };
    
    let totalScore = 0;
    let totalMetrics = 0;
    
    METRICS_DEFINITIONS.forEach(metric => {
      if (metrics[metric.id as keyof typeof metrics] !== undefined) {
        const value = metrics[metric.id as keyof typeof metrics];
        const rating = getRating(value!, metric.thresholds);
        totalScore += scores[rating];
        totalMetrics++;
      }
    });
    
    return totalMetrics > 0 ? Math.round(totalScore / totalMetrics) : 0;
  };
  
  const getRating = (value: number | string, thresholds: MetricRatingThresholds): 'green' | 'yellow' | 'red' | 'unknown' => {
    if (typeof value === 'string' || typeof thresholds.green === 'string') {
      // Handle string-based ratings
      if (value === thresholds.green) return 'green';
      if (value === thresholds.yellow) return 'yellow';
      if (value === thresholds.red) return 'red';
      return 'unknown';
    }
    
    const numValue = typeof value === 'number' ? value : parseFloat(value);
    
    if (isNaN(numValue)) return 'unknown';
    
    const { higherIsBetter = true } = thresholds;
    
    // Check green threshold
    if (Array.isArray(thresholds.green)) {
      const [min, max] = thresholds.green;
      if (numValue >= min && numValue <= max) return 'green';
    } else if (typeof thresholds.green === 'number') {
      if (higherIsBetter && numValue >= thresholds.green) return 'green';
      if (!higherIsBetter && numValue <= thresholds.green) return 'green';
    }
    
    // Check yellow range
    if (Array.isArray(thresholds.yellow)) {
      const [min, max] = thresholds.yellow;
      if ((higherIsBetter && numValue >= min && numValue < max) ||
          (!higherIsBetter && numValue > min && numValue <= max)) {
        return 'yellow';
      }
    }
    
    // Check red threshold
    if (Array.isArray(thresholds.red)) {
      const [min, max] = thresholds.red;
      if (numValue >= min && numValue <= max) return 'red';
    } else if (typeof thresholds.red === 'number') {
      if (higherIsBetter && numValue <= thresholds.red) return 'red';
      if (!higherIsBetter && numValue >= thresholds.red) return 'red';
    }
    
    return 'unknown';
  };
  
  const healthScore = calculateHealthScore();
  const healthColor = 
    healthScore >= 80 ? 'text-green-500' :
    healthScore >= 50 ? 'text-amber-500' :
    'text-red-500';
  
  if (compact) {
    console.log(`CampaignHealthScore: compact mode, score: ${healthScore}`);
    return (
      <div className="flex items-center">
        <div className={`text-sm font-medium ${healthColor}`}>
          Kampagnen-Gesundheit: {healthScore}%
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <button className="ml-1">
              <Info size={14} className="text-gray-400 hover:text-gray-300" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <h3 className="text-sm font-medium mb-2">Kampagnen-Metriken</h3>
            <div className="grid gap-1.5">
              {Object.entries(metrics).map(([key, value]) => {
                const metricDef = METRICS_DEFINITIONS.find(m => m.id === key);
                if (!metricDef || value === undefined) return null;
                
                return (
                  <div key={key} className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{metricDef.name}</span>
                    <MetricRating 
                      metricId={key} 
                      value={value}
                      size="sm"
                      showLabel={false}
                    />
                  </div>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Kampagnen-Gesundheit</h3>
        <div className={`text-sm font-bold ${healthColor}`}>
          {healthScore}%
        </div>
      </div>
      
      <div className="grid gap-2.5">
        {Object.entries(metrics).map(([key, value]) => {
          if (value === undefined) return null;
          
          return (
            <div key={key} className="flex items-center justify-between">
              {showLabels ? (
                <MetricRating 
                  metricId={key} 
                  value={value}
                  showLabel={true}
                  size="sm"
                />
              ) : (
                <>
                  <span className="text-xs text-gray-500">
                    {METRICS_DEFINITIONS.find(m => m.id === key)?.name || key}
                  </span>
                  <MetricRating 
                    metricId={key} 
                    value={value}
                    size="sm"
                  />
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const MetricsTable: React.FC<{
  metrics: {
    cpm?: number;
    cpc?: number;
    ctr?: number;
    conversionRate?: number;
    cpl?: number;
    roas?: number;
    frequency?: number;
    adRelevance?: string;
  };
}> = ({ metrics }) => {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-800">
        <thead>
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
              Metrik
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
              Wert
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
              Status
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
              Schwellenwerte
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-800">
          {METRICS_DEFINITIONS.map(metric => {
            const value = metrics[metric.id as keyof typeof metrics];
            if (value === undefined) return null;
            
            const rating = getRating(value, metric.thresholds);
            
            const formatThresholds = (thresholds: MetricRatingThresholds) => {
              const { unit = '' } = thresholds;
              
              const formatValue = (val: number | string | [number, number]) => {
                if (typeof val === 'string') return val;
                if (Array.isArray(val)) return `${val[0]}${unit} - ${val[1]}${unit}`;
                return `${val}${unit}`;
              };
              
              if (thresholds.higherIsBetter === false) {
                return (
                  <>
                    <span className="text-green-500">≤ {formatValue(thresholds.green)}</span> |{' '}
                    <span className="text-amber-500">{formatValue(thresholds.yellow)}</span> |{' '}
                    <span className="text-red-500">≥ {formatValue(thresholds.red)}</span>
                  </>
                );
              } else {
                return (
                  <>
                    <span className="text-green-500">≥ {formatValue(thresholds.green)}</span> |{' '}
                    <span className="text-amber-500">{formatValue(thresholds.yellow)}</span> |{' '}
                    <span className="text-red-500">≤ {formatValue(thresholds.red)}</span>
                  </>
                );
              }
            };
            
            return (
              <tr key={metric.id}>
                <td className="px-4 py-3 text-sm">
                  <div className="font-medium">{metric.name}</div>
                  <div className="text-xs text-gray-500">{metric.description}</div>
                </td>
                <td className="px-4 py-3 text-sm">
                  {typeof value === 'number' 
                    ? formatMetricValue(value, metric.thresholds.unit || '')
                    : value}
                </td>
                <td className="px-4 py-3">
                  <MetricRating metricId={metric.id} value={value} size="sm" showValue={false} />
                </td>
                <td className="px-4 py-3 text-xs">
                  {formatThresholds(metric.thresholds)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Helper functions
const formatMetricValue = (value: number, unit: string): string => {
  // Format based on unit
  if (unit === '%') {
    return `${value.toFixed(1)}${unit}`;
  } else if (unit === '€') {
    return `${unit}${value.toFixed(2)}`;
  } else if (unit === ':1') {
    return `${value.toFixed(1)}${unit}`;
  }
  
  return `${value}${unit}`;
};

function getRating(value: any, thresholds: MetricRatingThresholds): 'green' | 'yellow' | 'red' | 'unknown' {
  if (typeof value === 'string' || typeof thresholds.green === 'string') {
    // Handle string-based ratings
    if (value === thresholds.green) return 'green';
    if (value === thresholds.yellow) return 'yellow';
    if (value === thresholds.red) return 'red';
    return 'unknown';
  }
  
  const numValue = typeof value === 'number' ? value : parseFloat(value);
  
  if (isNaN(numValue)) return 'unknown';
  
  const { higherIsBetter = true } = thresholds;
  
  // Check green threshold
  if (Array.isArray(thresholds.green)) {
    const [min, max] = thresholds.green;
    if (numValue >= min && numValue <= max) return 'green';
  } else if (typeof thresholds.green === 'number') {
    if (higherIsBetter && numValue >= thresholds.green) return 'green';
    if (!higherIsBetter && numValue <= thresholds.green) return 'green';
  }
  
  // Check yellow range
  if (Array.isArray(thresholds.yellow)) {
    const [min, max] = thresholds.yellow;
    if ((higherIsBetter && numValue >= min && numValue < max) ||
        (!higherIsBetter && numValue > min && numValue <= max)) {
      return 'yellow';
    }
  }
  
  // Check red threshold
  if (Array.isArray(thresholds.red)) {
    const [min, max] = thresholds.red;
    if (numValue >= min && numValue <= max) return 'red';
  } else if (typeof thresholds.red === 'number') {
    if (higherIsBetter && numValue <= thresholds.red) return 'red';
    if (!higherIsBetter && numValue >= thresholds.red) return 'red';
  }
  
  return 'unknown';
}
